import _ from 'lodash';
import { extendMoment } from 'moment-range';
import Moment from 'moment';
import React from 'react';
const moment = extendMoment(Moment);
import 'moment-timezone';
/** import third-party libraries in the section above, Sort Lines Ascending, expect moment extension and timezone` */

import Typography from '@mui/material/Typography';
/** import mui components in the section above, Sort Lines Ascending */

import { hasTopLevelFieldsBeenModified } from 'widgets/history-table';
import { IncludeExcludeLayout } from 'widgets-v6/layout';
import Separator from 'widgets-v5/separator';
import Spacer from 'widgets-v5/spacer';
/** import widgets components in the section above, Sort Lines Ascending */

import {
    calculateThirdPartyFees,
    formatBillableVolume,
    getDoohPublisherNamesFromIds,
    isTacticsEnabled,
} from 'states/resources/ads/business-logic';
import {
    checkOnlyCreativeWeightChanges,
    formatCustomFieldProperties,
    formatFrequencyCapFromSnapshot,
    hasChangedBillableImpressions,
    hasChangedNonBillableImpressions,
    hasCustomFieldChanged,
    hasFrequencyCapVisibleChange,
    resolveCustomFieldForHistory,
    resolvePreviousCustomFieldForHistory,
} from './helpers';
import { convertDaypartsByTimezone, mergeTimeranges } from 'common/fns';
import {
    creativeHistoryClipboardGroups,
    creativeHistoryName,
    formatCreativeHistoryClipboardText,
    renderRuleCreativeHistory,
    renderScheduledCreativeHistory,
    renderScheduleWindow,
    renderSingleCreativeHistory,
} from './scheduled-creative-history';
import { formatNumber_whole, formatNumber_currency } from 'utils/formatting';
import {
    formatWeatherConditionsClipboardText,
    WeatherConditionHistorySummary,
} from './weather-conditions-history-summary';
import {
    getConditionLabel,
    getHasWeatherConditions,
    getInputAdornmentMap,
    getIsWeatherTypeTemperature,
    getQualifierLabel,
    getTimeframeLabel,
    getValueLabel,
} from 'states/resources/weather-conditions/selectors';
import {
    getTargetCarriersExclude,
    getTargetCarriersInclude,
} from 'common/constants/carrier-targeting-v2';
import { IAS_CONTEXTUAL_TARGETING_MAPPING } from 'common/constants/ias-contextual-targeting';
import formatGeoCountry, { getGeoCountryClipboardLines } from 'services/gis/geo-country-format';
/** import multiple application imports in the section above, Sort Lines Ascending */

import { CreativeRotationMethodMapping } from 'states/resources/ads/business-logic';
import { DOOH_VENUE_TYPES_NO_PARENT } from 'common/constants/dooh-venue-types';
import { formatDate_long } from 'utils/formatting';
import { getConversionsForCreativeOptimization } from 'containers/ad-summary';
import { getEnvironmentSettings } from 'services/environment';
import { IAS_BRAND_SAFETY_MAPPING } from 'common/constants/ias-brand-safety';
import { IAS_FRAUD_PREVENTION_RISK_MAPPING } from 'common/constants/ias-fraud-prevention';
import { IAS_GROUPM_VIEWABILITY_MAPPING } from 'common/constants/ias-groupm-viewability';
import { IAS_PUBLICIS_VIEWABILITY_MAPPING } from 'common/constants/ias-publicis-viewability';
import { IAS_VIEWABILITY_OPTIONS } from 'common/constants/ias-viewability';
import { labelExchange } from 'utils/formatting';
import { WeatherRotationRuleSummary } from './weather-rotation-rule-summary';
import CARRIER_TARGETING from 'common/constants/carrier-targeting';
import flags from 'containers/flags/service';
import IABCATEGORY_TARGETING from 'common/constants/iab-categories';
import LANGUAGE_CODES from 'common/constants/language-codes';
import PLATFORM from 'common/constants/platforms';
/** import single application imports in the section above, Sort Lines Ascending */

const BILLING_RATES = {
    CPM: 'cpm_rate',
    CPC: 'cpc_rate',
    billable_media_cost_markup: 'billable_media_cost_markup_rate',
    billable_media_cost_margin: 'billable_media_cost_margin_rate',
};

const REVENUE_MODEL = {
    cpmcpc: 'CPM/CPC',
    cpm: 'CPM',
    agencyMargin: 'Agency Margin',
    totalSpendMarkup: 'Total Cost Markup',
    disabled: 'Disabled',
};

/**
 * Joins list items one per line for the History Copy button. On-screen history keeps its commas.
 *
 * @param {Array<string>} items - List items in display order.
 * @returns {string} One item per line, with empty entries dropped.
 */
const joinClipboardLines = items => (items ?? []).filter(Boolean).join('\n');

/**
 * Names of the app lists whose ids are in `listIds`. Missing lists are dropped.
 *
 * @param {Object} props
 * @param {Array} props.listIds - App list ids stored on the ad.
 * @param {Array<{id: *, name: string}>} props.appLists - Loaded app lists.
 * @returns {string[]} Display names.
 */
const getApplistNames = ({ listIds, appLists }) =>
    (listIds ?? [])
        .map(value => {
            const appList = (appLists ?? []).find(list => list.id === value);
            if (appList) {
                return appList.name;
            }
            return '';
        })
        .filter(Boolean);

/**
 * On-screen Apps and Sites text. Names stay comma-separated.
 *
 * @param {Object} props
 * @param {Array} props.listIds - App list ids stored on the ad.
 * @param {Array<{id: *, name: string}>} props.appLists - Loaded app lists.
 * @param {string} props.emptyMessage - Shown when no lists match.
 * @returns {string}
 */
const formatApplistEntries = ({ listIds, appLists, emptyMessage }) => {
    const formattedLists = getApplistNames({ listIds, appLists }).join(', ');

    // If the string is empty, return the fallback message
    return formattedLists === '' ? emptyMessage : formattedLists;
};

/**
 * Clipboard text for an Apps and Sites list. One name per line.
 * An empty list stays the single-line fallback message.
 *
 * @param {Object} props
 * @param {Array} props.listIds - App list ids stored on the ad.
 * @param {Array<{id: *, name: string}>} props.appLists - Loaded app lists.
 * @param {string} props.emptyMessage - Shown when no lists match.
 * @returns {string}
 */
const getApplistClipboardText = ({ listIds, appLists, emptyMessage }) => {
    const names = getApplistNames({ listIds, appLists });
    return names.length === 0 ? emptyMessage : joinClipboardLines(names);
};

const getTargetCarriers = obj => {
    return _.isArray(obj)
        ? _.map(obj, function(i) {
              return CARRIER_TARGETING[i];
          }).join(', ')
        : '';
};

function getBillingRate(snapshot) {
    return snapshot[BILLING_RATES[snapshot.ef_billing_terms]];
}

const rotationMethodMapping = mode => {
    switch (mode) {
        case CreativeRotationMethodMapping.Single:
            return 'Single Creative';
        case CreativeRotationMethodMapping.Even:
            return 'Even Rotation';
        case CreativeRotationMethodMapping.Weighted:
            return 'Weighted Rotation';
        case CreativeRotationMethodMapping.Scheduled:
            return 'Scheduled Rotation';
        case CreativeRotationMethodMapping.OptimizedForClicks:
            return 'Optimize For Clicks';
        case CreativeRotationMethodMapping.OptimizedForConversions:
            return 'Optimize For Conversions';
        case CreativeRotationMethodMapping.OptimizedForVideoCompletion:
            return 'Optimize For Video Completion';
        case CreativeRotationMethodMapping.Weather:
            return 'Weather Condition';
        default:
            return '';
    }
};

/**
 * Multiline clipboard text for one creative snapshot.
 * Includes the rotation method and one group per schedule window or weather rule.
 *
 * @param {Object} snapshot - Ad history snapshot.
 * @param {Object} creatives - Live creatives keyed by markup id.
 * @param {Object} weatherConditionsData - Bound weather label functions.
 * @returns {string}
 */
function creativeClipboardForSnapshot(snapshot, creatives, weatherConditionsData) {
    return formatCreativeHistoryClipboardText({
        rotationMethodLabel: rotationMethodMapping(
            snapshot?.rotation_rules?.mode ?? CreativeRotationMethodMapping.Single
        ),
        groups: creativeHistoryClipboardGroups({
            snapshot,
            creatives,
            weatherConditionsData,
        }),
    });
}
export default function selector(storeState, props) {
    const { adId, campaignId } = props.params;
    const historyData = _.get(storeState, `resources.history.ads.${adId}`, []);
    const adName = _.get(storeState, `resources.ads.${adId}.attr.name`, '');
    const ad = _.get(storeState, `resources.ads.${adId}.attr`);
    const campaign = _.get(storeState, `resources.campaigns.${campaignId}.attr`);
    const organizationTimezone = _.get(storeState, 'profile.organizationTimezone');
    const ftaLocationLists = _.get(storeState, 'adHistory.ftaLocationLists');
    const organization = _.get(storeState, 'adHistory.organization');

    const ftaLocationListsMapping = {};
    _.each(ftaLocationLists, locationList => {
        ftaLocationListsMapping[locationList.id] = locationList.name;
    });

    const fields = getFields({
        storeState,
        ad,
        campaign,
        ftaLocationListsMapping,
        organization,
    });

    // History is newest-first; attach the chronologically older snapshot for diff helpers.
    const historyDataWithContext = historyData.map((entry, index) => ({
        ...entry,
        _previousSnapshot: index + 1 < historyData.length ? historyData[index + 1].snapshot : null,
    }));

    const filteredHistoryData = _(historyDataWithContext)
        .filter((entry, index) => {
            if (index === 0) {
                return true;
            }

            const currenyHistoryValue = JSON.stringify(_.omit(entry.snapshot, ['_etag']));
            const prevHistoryValue = JSON.stringify(
                _.omit(historyDataWithContext[index - 1].snapshot, ['_etag'])
            );

            return currenyHistoryValue !== prevHistoryValue;
        })
        .filter(entry => {
            const allChanges = _.map(fields, field => {
                return !!field.hasChange(entry);
            });

            const hasNoChanges = _.every(allChanges, change => change === false);

            return !hasNoChanges;
        })
        .value();

    const out = {
        adName,
        historyEntries: filteredHistoryData,
        fields,
        organizationTimezone,
        organizationId: _.get(storeState, 'profile.organizationId'),
        isLoading: _.get(storeState, 'adHistory.isLoading', false),
    };
    return out;
}

function getFields({ storeState, ad, campaign, ftaLocationListsMapping }) {
    const environmentSettings = getEnvironmentSettings();
    if (!ad || !campaign) {
        return [];
    }

    const isDOOHAd = Array.isArray(ad.platforms) && ad.platforms.includes('dooh');
    const isCTVAd = Array.isArray(ad.platforms) && ad.platforms.includes('ctv');

    const canUseIas = !isDOOHAd && !isCTVAd && environmentSettings.canUseIas();

    const fields = [].concat(
        [
            {
                label: 'Basic',
                isCategory: true,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, [
                        'platform',
                        'name',
                        'paused',
                        'reference_id',
                        'notes',
                    ]);
                },
                format() {
                    return '';
                },
            },
            {
                label: 'Platform',
                isCategory: false,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['platform']);
                },
                format(entry) {
                    let platform = _.find(PLATFORM, item => {
                        if (entry.snapshot.platform === item.value) {
                            return item;
                        }
                    });
                    return platform.label;
                },
                getClipboardText(entry) {
                    return this.format(entry);
                },
            },
            {
                label: 'Name',
                isCategory: false,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['name']);
                },
                format(entry) {
                    return entry.snapshot.name;
                },
                getClipboardText(entry) {
                    return this.format(entry);
                },
            },
            {
                label: 'Status',
                isCategory: false,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['paused']);
                },
                format(entry) {
                    return entry.snapshot.paused ? 'Off' : 'On';
                },
                getClipboardText(entry) {
                    return this.format(entry);
                },
            },
            {
                label: 'Reference ID',
                isCategory: false,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['reference_id']);
                },
                format(entry) {
                    return entry.snapshot.reference_id;
                },
                getClipboardText(entry) {
                    return this.format(entry);
                },
            },
        ],
        // Custom fields: category header plus one detail row per org field (name, key, value, required).
        ...(ad.custom_fields && ad.custom_fields.length > 0
            ? [
                  {
                      label: 'Custom Field',
                      isCategory: true,
                      hasChange(entry) {
                          return ad.custom_fields.some((unused, customFieldIndex) =>
                              hasCustomFieldChanged(entry, customFieldIndex)
                          );
                      },
                      format() {
                          return '';
                      },
                  },
                  ..._.map(ad.custom_fields, (adCustomField, customFieldIndex) => {
                      // From/To lists every sub-property (name, key, value, required).
                      const renderCustomFieldChange = (entry, side) => {
                          const field =
                              side === 'previous'
                                  ? resolvePreviousCustomFieldForHistory(
                                        entry,
                                        customFieldIndex,
                                        adCustomField
                                    )
                                  : resolveCustomFieldForHistory(
                                        entry,
                                        customFieldIndex,
                                        adCustomField
                                    );

                          return (
                              <div style={{ whiteSpace: 'pre-wrap' }}>
                                  {formatCustomFieldProperties(field)}
                              </div>
                          );
                      };

                      return {
                          label: adCustomField.name,
                          isCategory: false,
                          useClipboardText: true,
                          // Still render when patch changed but formatted From/To text matches.
                          keepDetailRowWhenUnchanged: true,
                          hasChange(entry) {
                              return hasCustomFieldChanged(entry, customFieldIndex);
                          },
                          // From column uses _previousSnapshot on the selected entry (see history-table).
                          formatPrevious(entry) {
                              return renderCustomFieldChange(entry, 'previous');
                          },
                          format(entry) {
                              return renderCustomFieldChange(entry, 'current');
                          },
                          /**
                           * Copies the new custom field values only, one property per line.
                           *
                           * @param {Object} entry - History entry for the current snapshot.
                           * @returns {string}
                           */
                          getClipboardText(entry) {
                              const currentField = resolveCustomFieldForHistory(
                                  entry,
                                  customFieldIndex,
                                  adCustomField
                              );

                              return [
                                  `Field name: ${adCustomField.name}`,
                                  formatCustomFieldProperties(currentField),
                              ].join('\n');
                          },
                      };
                  }),
              ]
            : []),
        [
            {
                label: 'Notes',
                isCategory: false,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['notes']);
                },
                format(entry) {
                    return entry.snapshot.notes;
                },
                getClipboardText(entry) {
                    return this.format(entry);
                },
            },
            {
                label: 'Schedule',
                isCategory: true,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, [
                        'timezone',
                        'start',
                        'end',
                        'dayparts',
                        'dayparts_local',
                        'weekparts',
                        'weekparts_local',
                    ]);
                },
                format() {
                    return '';
                },
            },
            {
                label: 'Timezone',
                isCategory: false,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['timezone']);
                },
                format(entry) {
                    return entry.snapshot.timezone;
                },
                getClipboardText(entry) {
                    return this.format(entry);
                },
            },
            {
                label: 'Start Date',
                isCategory: false,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['start']);
                },
                format(entry) {
                    const { start, timezone } = entry.snapshot;
                    return formatDate_long(start, timezone);
                },
                getClipboardText(entry) {
                    return this.format(entry);
                },
            },
            {
                label: 'End Date',
                isCategory: false,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['end']);
                },
                format(entry) {
                    const { end, timezone } = entry.snapshot;
                    return formatDate_long(end, timezone);
                },
                getClipboardText(entry) {
                    return this.format(entry);
                },
            },
            {
                label: 'Dayparts',
                isCategory: false,
                useClipboardText: true,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, [
                        'dayparts',
                        'dayparts_local',
                    ]);
                },
                format(entry) {
                    const { dayparts_local, timezone, start } = entry.snapshot;

                    return formatDayparts(
                        entry.snapshot,
                        _.isArray(dayparts_local) ? 'UTC' : timezone,
                        start
                    );
                },
                getClipboardText(entry) {
                    const { dayparts_local, timezone, start } = entry.snapshot;

                    return formatDaypartsClipboardText(
                        entry.snapshot,
                        Array.isArray(dayparts_local) ? 'UTC' : timezone,
                        start
                    );
                },
            },
            {
                label: 'Weekparts',
                isCategory: false,
                useClipboardText: true,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, [
                        'weekparts',
                        'weekparts_local',
                    ]);
                },
                format(entry) {
                    const { weekparts, weekparts_local } = entry.snapshot;

                    // use weekparts_local when weepparts is null
                    const _weekparts = weekparts || weekparts_local;

                    return formatWeekparts(_weekparts, weekparts ? '(UTC) ' : '(Local Time) ');
                },
                getClipboardText(entry) {
                    const { weekparts, weekparts_local } = entry.snapshot;

                    return getWeekpartsClipboardText(
                        weekparts || weekparts_local,
                        weekparts ? '(UTC) ' : '(Local Time) '
                    );
                },
            },
            {
                label: 'Revenue',
                isCategory: true,
                hasChange(entry) {
                    if (
                        hasChangedBillableImpressions(entry) ||
                        hasChangedNonBillableImpressions(entry)
                    ) {
                        return true;
                    }

                    return hasTopLevelFieldsBeenModified(entry.patch, [
                        'billing_rate',
                        'billing_term',
                        'billing_enabled',
                        'campaign_budget_enabled',
                        'campaign_max_total_spend_local',
                    ]);
                },
                format() {
                    return '';
                },
            },
            campaign.revenueModel !== 'agencyMargin' && {
                label: 'Bonus Ad',
                isCategory: false,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['billing_enabled']);
                },
                format(entry) {
                    return entry.snapshot.billing_enabled ? 'Yes' : 'No';
                },
            },
            {
                label: 'Revenue Model',
                isCategory: false,
                hasChange(entry) {
                    const intersection = _.intersection(_.keys(entry.patch), [
                        'billing_rate',
                        'billing_term',
                        'billing_enabled',
                        'campaign_budget_enabled',
                        'campaign_max_total_spend_local',
                    ]);

                    return intersection.length > 0;
                },
                format() {
                    return REVENUE_MODEL[campaign.revenueModel];
                },
            },
            {
                label: campaign.revenueModel === 'agencyMargin' ? 'Agency Margin' : 'Billing Rate',
                isCategory: false,
                hasChange(entry) {
                    const intersection = _.intersection(_.keys(entry.patch), [
                        'billing_rate',
                        'billing_term',
                        'billing_enabled',
                        'campaign_budget_enabled',
                        'campaign_max_total_spend_local',
                    ]);

                    return intersection.length > 0;
                },
                format(entry) {
                    const { snapshot } = entry;
                    const billingRate = getBillingRate(snapshot);

                    let value = '';
                    if (campaign.revenueModel === 'agencyMargin') {
                        value = `${billingRate * 100}%`;
                        return value;
                    }
                    if (campaign.revenueModel !== 'disabled') {
                        switch (snapshot.ef_billing_terms) {
                            case 'CPM': {
                                value = `${formatNumber_currency(billingRate)} CPM`;
                                break;
                            }
                            case 'CPC': {
                                value = `${formatNumber_currency(billingRate)} CPC`;
                                break;
                            }
                        }
                    }
                    return value;
                },
            },
            campaign.revenueModel !== 'agencyMargin' && {
                label: 'Billable Impressions',
                isCategory: false,
                hasChange(entry) {
                    if (hasChangedBillableImpressions(entry)) {
                        return true;
                    }

                    const intersection = _.intersection(_.keys(entry.patch), [
                        'billing_rate',
                        'billing_term',
                        'billing_enabled',
                        'campaign_budget_enabled',
                        'campaign_max_total_spend_local',
                    ]);

                    return intersection.length > 0;
                },
                format(entry) {
                    const { snapshot } = entry;
                    const isUnlimited = snapshot.billable_volume === -1;

                    if (isUnlimited) {
                        return 'Unlimited';
                    }

                    return formatBillableVolume(
                        snapshot.billable_volume,
                        snapshot.ef_billing_terms
                    );
                },
            },
            campaign.revenueModel !== 'agencyMargin' && {
                label:
                    campaign.revenueModel === 'disabled'
                        ? 'Total Impressions'
                        : 'Non-billable Impressions',
                isCategory: false,
                hasChange(entry) {
                    if (hasChangedNonBillableImpressions(entry)) {
                        return true;
                    }

                    const intersection = _.intersection(_.keys(entry.patch), [
                        'billing_rate',
                        'billing_term',
                        'billing_enabled',
                        'campaign_budget_enabled',
                        'campaign_max_total_spend_local',
                    ]);

                    return intersection.length > 0;
                },
                format(entry) {
                    const { snapshot } = entry;

                    return formatBillableVolume(
                        snapshot.non_billable_volume,
                        snapshot.ef_billing_terms
                    );
                },
            },
            {
                label: 'Budget',
                isCategory: true,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, [
                        'billing_rate',
                        'billing_term',
                        'billing_enabled',
                        'campaign_budget_enabled',
                        'campaign_max_total_spend_local',
                    ]);
                },
                format() {
                    return '';
                },
            },
            campaign.revenueModel !== 'agencyMargin' && {
                label: 'Total Impressions',
                isCategory: false,
                hasChange(entry) {
                    const intersection = _.intersection(_.keys(entry.patch), [
                        'billing_rate',
                        'billing_term',
                        'billing_enabled',
                        'campaign_budget_enabled',
                        'campaign_max_total_spend_local',
                    ]);

                    return intersection.length > 0;
                },
                format(entry) {
                    const { snapshot } = entry;
                    const isUnlimited = snapshot.billable_volume === -1;
                    let value = '';

                    if (campaign.revenueModel !== 'disabled') {
                        value = `${
                            isUnlimited
                                ? 'Unlimited'
                                : formatNumber_whole(
                                      snapshot.billable_volume + snapshot.non_billable_volume
                                  )
                        }`;
                    }
                    return value;
                },
            },
            {
                label:
                    campaign.revenueModel !== 'agencyMargin' ? 'Total Revenue' : 'Revenue Budget',
                isCategory: false,
                hasChange(entry) {
                    const intersection = _.intersection(_.keys(entry.patch), [
                        'billing_rate',
                        'billing_term',
                        'billing_enabled',
                        'campaign_budget_enabled',
                        'campaign_max_total_spend_local',
                    ]);

                    return intersection.length > 0;
                },
                format(entry) {
                    const { snapshot } = entry;
                    const billingRate = getBillingRate(snapshot);
                    const isUnlimited = snapshot.billable_volume === -1;

                    let value = '';

                    switch (snapshot.ef_billing_terms) {
                        case 'CPM': {
                            value = `${
                                isUnlimited
                                    ? 'Unlimited'
                                    : formatNumber_currency(
                                          (snapshot.billable_volume / 1000) * billingRate
                                      )
                            }`;
                            break;
                        }
                        case 'CPC': {
                            value = `Total ${'Revenue'} : ${
                                isUnlimited
                                    ? 'Unlimited'
                                    : formatNumber_currency(snapshot.billable_volume * billingRate)
                            }`;
                            break;
                        }
                        case 'billable_media_cost_margin': {
                            const totalBillings = snapshot.billable_volume;

                            value = `${
                                isUnlimited ? 'Unlimited' : formatNumber_currency(totalBillings)
                            }`;
                            break;
                        }
                    }

                    return value;
                },
            },
            campaign.revenueModel === 'agencyMargin' && {
                label: 'Total Cost Budget',
                isCategory: false,
                hasChange(entry) {
                    const intersection = _.intersection(_.keys(entry.patch), [
                        'billing_rate',
                        'billing_term',
                        'billing_enabled',
                        'campaign_budget_enabled',
                        'campaign_max_total_spend_local',
                    ]);

                    return intersection.length > 0;
                },
                format(entry) {
                    const { snapshot } = entry;
                    const billingRate = getBillingRate(snapshot);
                    const isUnlimited = snapshot.billable_volume === -1;

                    let value = '';

                    if (snapshot.ef_billing_terms === 'billable_media_cost_margin') {
                        const billableMediaSpend = snapshot.billable_volume * (1 - billingRate);

                        value = `${
                            isUnlimited ? 'Unlimited' : formatNumber_currency(billableMediaSpend)
                        }`;
                    }
                    return value;
                },
            },
            {
                label: 'Third Party Fees',
                isCategory: false,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['thirdPartyFees']);
                },
                format(entry) {
                    const { snapshot } = entry;
                    let value = {
                        label: ` ${formatNumber_currency(calculateThirdPartyFees(snapshot))} CPM`,
                    };
                    return value.label;
                },
                getClipboardText(entry) {
                    return this.format(entry);
                },
            },
            {
                label: 'Pacing',
                isCategory: true,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['use_front_load_pacing']);
                },
                format() {
                    return '';
                },
            },
            {
                label: 'Pacing Strategy',
                isCategory: false,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['use_front_load_pacing']);
                },
                format(entry) {
                    const { use_front_load_pacing } = entry.snapshot;
                    if (!use_front_load_pacing) {
                        return 'Even Pacing: Impressions';
                    }
                    return 'Front-loaded Pacing: Impressions';
                },
                getClipboardText(entry) {
                    return this.format(entry);
                },
            },

            {
                label: 'Daily Pacing',
                isCategory: false,
                useClipboardText: true,
                hasChange(entry) {
                    return (
                        hasTopLevelFieldsBeenModified(entry.patch, ['use_front_load_pacing']) ||
                        hasTopLevelFieldsBeenModified(entry.patch, [
                            'max_daily_impressions',
                            'max_daily_clicks',
                            'max_daily_spend_local',
                            'max_daily_billings',
                        ])
                    );
                },
                prepData(entry) {
                    const { snapshot } = entry;

                    let impressionsLabel;
                    if (snapshot.max_daily_impressions) {
                        impressionsLabel = formatNumber_whole(snapshot.max_daily_impressions);
                    } else {
                        impressionsLabel = 'Auto';
                    }

                    let clicksLabel;
                    if (snapshot.max_daily_clicks) {
                        clicksLabel = formatNumber_whole(snapshot.max_daily_clicks);
                    } else {
                        clicksLabel = 'Auto';
                    }

                    let spendLabel;
                    if (snapshot.max_daily_spend_local) {
                        spendLabel = formatNumber_currency(snapshot.max_daily_spend_local);
                    } else {
                        spendLabel = 'Auto';
                    }

                    const labels = [`Impressions: ${impressionsLabel}`];

                    if (!isDOOHAd && !isCTVAd) {
                        labels.push(`Clicks: ${clicksLabel}`);
                    }

                    labels.push(`Total Cost : ${spendLabel}`);

                    return snapshot.use_front_load_pacing ? ['N/A'] : labels;
                },
                format(entry) {
                    const data = this.prepData(entry);

                    return (
                        <div>
                            {_.map(data, label => (
                                <div key={label}>{label}</div>
                            ))}
                        </div>
                    );
                },
                getClipboardText(entry) {
                    return joinClipboardLines(this.prepData(entry));
                },
            },
            environmentSettings.canUseFta() && {
                label: 'FTA',
                isCategory: true,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['fta']);
                },
                format() {
                    return '';
                },
            },
            environmentSettings.canUseFta() && {
                label: 'Foot Traffic Attribution',
                isCategory: false,
                useClipboardText: true,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['fta']);
                },
                format(entry) {
                    const { fta } = entry.snapshot;
                    if (!fta || fta.enabled === null) {
                        return 'N/A';
                    }

                    return (
                        <table>
                            <tbody>
                                <tr>
                                    <td>{fta.enabled ? 'Enabled' : 'Disabled'}</td>
                                </tr>
                                <tr>
                                    <td>Location List: </td>
                                    <td>{ftaLocationListsMapping[fta.location_list] || 'N/A'}</td>
                                </tr>
                                <tr>
                                    <td>Partner ID: </td>
                                    <td>{fta.partner_id || 'N/A'}</td>
                                </tr>
                            </tbody>
                        </table>
                    );
                },
                getClipboardText(entry) {
                    const { fta } = entry.snapshot;
                    if (!fta || fta.enabled === null) {
                        return 'N/A';
                    }

                    return [
                        fta.enabled ? 'Enabled' : 'Disabled',
                        `Location List: ${ftaLocationListsMapping[fta.location_list] || 'N/A'}`,
                        `Partner ID: ${fta.partner_id || 'N/A'}`,
                    ].join('\n');
                },
            },
            {
                label: 'Creative',
                isCategory: true,
                hasChange(entry) {
                    const hasChange = hasTopLevelFieldsBeenModified(entry.patch, [
                        'creative',
                        'rotation_rules',
                    ]);

                    const onlyWeightCreativeChanges = checkOnlyCreativeWeightChanges(
                        entry.patch,
                        entry.snapshot
                    );
                    return hasChange && !onlyWeightCreativeChanges;
                },
                format() {
                    return '';
                },
            },
            {
                label: 'Creative',
                isCategory: false,
                highlight: 'words',
                useClipboardText: true,
                hasChange(entry) {
                    const hasChange = hasTopLevelFieldsBeenModified(entry.patch, [
                        'creative',
                        'rotation_rules',
                    ]);

                    const onlyWeightCreativeChanges = checkOnlyCreativeWeightChanges(
                        entry.patch,
                        entry.snapshot
                    );
                    return hasChange && !onlyWeightCreativeChanges;
                },
                format(entry) {
                    const creatives = _.get(storeState, 'resources.creatives', {});
                    const { creative } = entry.snapshot;
                    const mode = _.get(
                        entry.snapshot,
                        'rotation_rules.mode',
                        CreativeRotationMethodMapping.Single
                    );
                    const rotationRules = _.get(entry, 'snapshot.rotation_rules');

                    if (
                        _.includes(
                            [
                                CreativeRotationMethodMapping.Weighted,
                                CreativeRotationMethodMapping.Even,
                                CreativeRotationMethodMapping.OptimizedForClicks,
                                CreativeRotationMethodMapping.OptimizedForConversions,
                                CreativeRotationMethodMapping.OptimizedForVideoCompletion,
                            ],
                            mode
                        )
                    ) {
                        const includeWeight = !_.includes(
                            [
                                CreativeRotationMethodMapping.OptimizedForClicks,
                                CreativeRotationMethodMapping.OptimizedForConversions,
                                CreativeRotationMethodMapping.OptimizedForVideoCompletion,
                            ],
                            mode
                        );

                        return (
                            <div>
                                <div>
                                    {`Rotation Method: ${rotationMethodMapping(mode)}`}
                                    <br />
                                    <br />
                                </div>
                                {renderRuleCreativeHistory({
                                    creatives,
                                    rules: _.get(entry.snapshot, 'rotation_rules.weighted', []),
                                    includeWeight,
                                })}
                                {_.map(rotationRules.scheduled, schedule =>
                                    renderScheduleWindow({
                                        schedule,
                                        creatives,
                                        timezone: entry.snapshot.timezone,
                                        rotationRules,
                                    })
                                )}
                            </div>
                        );
                    } else if (_.includes([CreativeRotationMethodMapping.Weather], mode)) {
                        const weatherRotationSettings = _.get(
                            entry.snapshot,
                            'rotation_rules.weather'
                        );
                        const weatherConditionsData = {
                            getConditionLabel: getConditionLabel(storeState),
                            getQualifierLabel: getQualifierLabel(storeState),
                            getValueLabel: getValueLabel(storeState),
                            getTimeframeLabel: getTimeframeLabel(storeState),
                            inputAdornmentMap: getInputAdornmentMap(storeState),
                            isWeatherTypeTemperature: getIsWeatherTypeTemperature(storeState),
                        };
                        const nonDefaultRules = (weatherRotationSettings ?? []).filter(
                            setting => !setting.isDefault
                        );
                        const defaultRules = (weatherRotationSettings ?? []).filter(
                            setting => setting.isDefault
                        );

                        return (
                            <div>
                                <Typography variant="subtitle1" gutterBottom>
                                    {`Rotation Method: ${rotationMethodMapping(mode)}`}
                                </Typography>
                                {nonDefaultRules.map((setting, index) => {
                                    return (
                                        <WeatherRotationRuleSummary
                                            key={setting.id}
                                            setting={setting}
                                            title={`Rule ${index + 1}`}
                                            weatherConditionsData={weatherConditionsData}
                                            creativeId={setting.markupId}
                                            creativeName={creativeHistoryName(
                                                creatives,
                                                setting.markupId
                                            )}
                                        />
                                    );
                                })}
                                {defaultRules.map(setting => {
                                    return (
                                        <WeatherRotationRuleSummary
                                            key={setting.id}
                                            setting={setting}
                                            title="Default Rule"
                                            weatherConditionsData={weatherConditionsData}
                                            creativeId={setting.markupId}
                                            creativeName={creativeHistoryName(
                                                creatives,
                                                setting.markupId
                                            )}
                                        />
                                    );
                                })}
                            </div>
                        );
                    } else if (
                        _.includes(
                            [
                                CreativeRotationMethodMapping.Single,
                                CreativeRotationMethodMapping.Scheduled,
                            ],
                            mode
                        )
                    ) {
                        return (
                            <div>
                                <div>
                                    {`Rotation Method: ${rotationMethodMapping(mode)}`}
                                    <br />
                                    <br />
                                </div>
                                {!rotationRules?.scheduled?.length
                                    ? renderSingleCreativeHistory({
                                          creatives,
                                          creativeIds: creative,
                                      })
                                    : renderScheduledCreativeHistory({
                                          rotationRules,
                                          creatives,
                                          timezone: entry.snapshot.timezone,
                                      })}
                            </div>
                        );
                    }
                },
                /**
                 * Creative copy text for the new values only.
                 * Each ID, Name, and Weight is on its own line. The previous snapshot is not included.
                 *
                 * @param {Object} entry - History entry for the current snapshot.
                 * @returns {string}
                 */
                getClipboardText(entry) {
                    const creatives = storeState?.resources?.creatives ?? {};
                    const weatherConditionsData = {
                        getConditionLabel: getConditionLabel(storeState),
                        getQualifierLabel: getQualifierLabel(storeState),
                        getValueLabel: getValueLabel(storeState),
                        getTimeframeLabel: getTimeframeLabel(storeState),
                        inputAdornmentMap: getInputAdornmentMap(storeState),
                        isWeatherTypeTemperature: getIsWeatherTypeTemperature(storeState),
                    };
                    return creativeClipboardForSnapshot(
                        entry.snapshot,
                        creatives,
                        weatherConditionsData
                    );
                },
            },
            {
                label: 'Conversion Optimization',
                isCategory: true,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, [
                        'conversionsForCreativeOptimization',
                    ]);
                },
                format() {
                    return '';
                },
            },
            {
                label: 'Optimized Conversion Events',
                isCategory: false,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, [
                        'conversionsForCreativeOptimization',
                    ]);
                },
                format(entry) {
                    const conversionsInCampaign = _.concat(
                        campaign.conversions,
                        campaign.advertiserConversions
                    );

                    return getConversionsForCreativeOptimization(
                        entry.snapshot.conversionsForCreativeOptimization,
                        conversionsInCampaign
                    ).toString();
                },
                getClipboardText(entry) {
                    return this.format(entry);
                },
            },
            canUseIas && {
                label: 'Third Party Verification',
                isCategory: true,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['forensiq_risk_cap']);
                },
                format() {
                    return '';
                },
            },
            canUseIas && {
                label: 'Fraud Prevention',
                isCategory: false,
                useClipboardText: true,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['ias']);
                },
                prepData(entry) {
                    const { snapshot } = entry;

                    let ipLabel = snapshot.ias.fraudPrevention.ip.value === 1 ? 'On' : 'Off';
                    let unratedLabel =
                        snapshot.ias.fraudPrevention.unrateable.value === 1 ? 'On' : 'Off';

                    const labels = [
                        `Content-based: ${
                            IAS_FRAUD_PREVENTION_RISK_MAPPING[
                                snapshot.ias.fraudPrevention.risk.value
                            ]
                        }`,
                        `Exclude Unrated content: ${ipLabel}`,
                        `Excloud fraudulent IP Addresses: ${unratedLabel}`,
                    ];

                    return snapshot.use_front_load_pacing ? ['N/A'] : labels;
                },
                format(entry) {
                    const data = this.prepData(entry);

                    return (
                        <div>
                            {_.map(data, label => (
                                <div key={label}>{label}</div>
                            ))}
                        </div>
                    );
                },

                /**
                 * Copies each Fraud Prevention line on its own line, matching the row.
                 *
                 * @param {Object} entry - History entry for the current snapshot.
                 * @returns {string}
                 */
                getClipboardText(entry) {
                    return joinClipboardLines(this.prepData(entry));
                },
            },
            canUseIas && {
                label: 'Viewability',
                isCategory: false,
                useClipboardText: true,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['ias']);
                },
                prepData(entry) {
                    const { snapshot } = entry;

                    const IAS_VIEWABILITY_OPTIONS_MAPPING = {};
                    _.each(IAS_VIEWABILITY_OPTIONS, option => {
                        IAS_VIEWABILITY_OPTIONS_MAPPING[option.value] = option;
                    });

                    const labels = [
                        `Viewability: ${
                            IAS_VIEWABILITY_OPTIONS_MAPPING[snapshot.ias.viewability.value].label
                        }`,
                        `GroupM Performance Matching: ${
                            IAS_GROUPM_VIEWABILITY_MAPPING[snapshot.ias.groupmViewability.value]
                        }`,
                        `Publicis Performance Matching: ${
                            IAS_PUBLICIS_VIEWABILITY_MAPPING[snapshot.ias.publicisViewability.value]
                        }`,
                    ];

                    return snapshot.use_front_load_pacing ? ['N/A'] : labels;
                },
                format(entry) {
                    const data = this.prepData(entry);

                    return (
                        <div>
                            {_.map(data, label => (
                                <div key={label}>{label}</div>
                            ))}
                        </div>
                    );
                },

                /**
                 * Copies each Viewability line on its own line, matching the row.
                 *
                 * @param {Object} entry - History entry for the current snapshot.
                 * @returns {string}
                 */
                getClipboardText(entry) {
                    return joinClipboardLines(this.prepData(entry));
                },
            },
            canUseIas && {
                label: 'Brand Safety',
                isCategory: false,
                useClipboardText: true,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['ias']);
                },
                prepData(entry) {
                    const { snapshot } = entry;

                    const labels = [
                        `Adult: ${IAS_BRAND_SAFETY_MAPPING[snapshot.ias.brandSafety.adult.value]}`,
                        `Alcohol: ${
                            IAS_BRAND_SAFETY_MAPPING[snapshot.ias.brandSafety.alcohol.value]
                        }`,
                        `Gambling: ${
                            IAS_BRAND_SAFETY_MAPPING[snapshot.ias.brandSafety.gambling.value]
                        }`,
                        `Hate Speech: ${
                            IAS_BRAND_SAFETY_MAPPING[snapshot.ias.brandSafety.hateSpeech.value]
                        }`,
                        `Illegal Downloads: ${
                            IAS_BRAND_SAFETY_MAPPING[
                                snapshot.ias.brandSafety.illegalDownloads.value
                            ]
                        }`,
                        `Illegal Drugs: ${
                            IAS_BRAND_SAFETY_MAPPING[snapshot.ias.brandSafety.illegalDrugs.value]
                        }`,
                        `Offensive Language: ${
                            IAS_BRAND_SAFETY_MAPPING[
                                snapshot.ias.brandSafety.offensiveLanguage.value
                            ]
                        }`,
                        `Violence: ${
                            IAS_BRAND_SAFETY_MAPPING[snapshot.ias.brandSafety.violence.value]
                        }`,
                        `Exclude unrated content: ${
                            snapshot.ias.brandSafety.illegalDrugs.value === 1 ? 'On' : 'Off'
                        }`,
                    ];

                    return snapshot.use_front_load_pacing ? ['N/A'] : labels;
                },
                format(entry) {
                    const data = this.prepData(entry);

                    return (
                        <div>
                            {_.map(data, label => (
                                <div key={label}>{label}</div>
                            ))}
                        </div>
                    );
                },
                /**
                 * Copies each Brand Safety line on its own line, matching the row.
                 *
                 * @param {Object} entry - History entry for the current snapshot.
                 * @returns {string}
                 */
                getClipboardText(entry) {
                    return joinClipboardLines(this.prepData(entry));
                },
            },
            {
                label: 'Targeting',
                isCategory: true,
                hasChange(entry) {
                    const fieldsToCheck = [
                        'geotargets',
                        'carriers_exclude',
                        'target_app_store_cat',
                        'target_iab_categories',
                        'target_iab_categories_exclude',
                        'audiences',
                        'audience_exclude',
                        'tactics_generators',
                        'weatherConditions',
                    ];

                    if (!isCTVAd) {
                        // fields only compatible with non-CTV ads
                        fieldsToCheck.push('geoboxes');
                    }

                    if (isDOOHAd) {
                        // fields only compatible with DOOH ads
                        fieldsToCheck.push('target_dooh_venues', 'target_dooh_venues_exclude');
                    }

                    if (!isDOOHAd) {
                        // fields only compatible with non-DOOH ads
                        fieldsToCheck.push('target_device_os', 'target_carriers');

                        if (!isCTVAd) {
                            // fields only compatible with non-DOOH and non-CTV ads
                            fieldsToCheck.push(
                                'target_device_language',
                                'target_device_language_exclude',
                                'target_genders',
                                'target_age_groups'
                            );
                        }
                    }

                    return hasTopLevelFieldsBeenModified(entry.patch, fieldsToCheck);
                },
                format() {
                    return '';
                },
            },
            {
                label: 'Geotargeting',
                isCategory: false,
                useClipboardText: true,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['geotargets']);
                },
                format(entry) {
                    return (
                        <div className="ef3-historyTable_bodyCell_geoTarget">
                            <ul>
                                {_.map(entry.snapshot.geotargets, obj =>
                                    formatGeoCountry(obj, true)
                                )}
                            </ul>
                        </div>
                    );
                },
                getClipboardText(entry) {
                    // Headings stay; each region or city is its own line.
                    return joinClipboardLines(
                        (entry.snapshot.geotargets ?? []).flatMap(obj =>
                            getGeoCountryClipboardLines(obj)
                        )
                    );
                },
            },
            {
                label: 'User Language',
                isCategory: false,
                useClipboardText: true,
                hasChange(entry) {
                    if (isDOOHAd || isCTVAd) {
                        return false;
                    }

                    return hasTopLevelFieldsBeenModified(entry.patch, ['target_device_language']);
                },
                format(entry) {
                    return formatDeviceLanguage(entry.snapshot.target_device_language);
                },
                getClipboardText(entry) {
                    return joinClipboardLines(
                        getDeviceLanguageItems(entry.snapshot.target_device_language)
                    );
                },
            },
            {
                label: 'User Language (Exclude)',
                isCategory: false,
                useClipboardText: true,
                hasChange(entry) {
                    if (isDOOHAd || isCTVAd) {
                        return false;
                    }

                    return hasTopLevelFieldsBeenModified(entry.patch, [
                        'target_device_language_exclude',
                    ]);
                },
                format(entry) {
                    return formatDeviceLanguage(entry.snapshot.target_device_language_exclude);
                },
                getClipboardText(entry) {
                    return joinClipboardLines(
                        getDeviceLanguageItems(entry.snapshot.target_device_language_exclude)
                    );
                },
            },
            {
                label: 'Device OS',
                isCategory: false,
                useClipboardText: true,
                hasChange(entry) {
                    if (isDOOHAd) {
                        return false;
                    }

                    return hasTopLevelFieldsBeenModified(entry.patch, ['target_device_os']);
                },
                format(entry) {
                    return [].concat(entry.snapshot.target_device_os).join(', ');
                },
                getClipboardText(entry) {
                    return joinClipboardLines([].concat(entry.snapshot.target_device_os));
                },
            },
            {
                label: 'Venue Type Include',
                isCategory: false,
                useClipboardText: true,
                hasChange(entry) {
                    if (!isDOOHAd) {
                        return false;
                    }

                    return hasTopLevelFieldsBeenModified(entry.patch, ['target_dooh_venues']);
                },
                format(entry) {
                    return formatDoohVenues(entry.snapshot.target_dooh_venues);
                },
                getClipboardText(entry) {
                    return joinClipboardLines(getDoohVenueItems(entry.snapshot.target_dooh_venues));
                },
            },
            {
                label: 'Venue Type Exclude',
                isCategory: false,
                useClipboardText: true,
                hasChange(entry) {
                    if (!isDOOHAd) {
                        return false;
                    }

                    return hasTopLevelFieldsBeenModified(entry.patch, [
                        'target_dooh_venues_exclude',
                    ]);
                },
                format(entry) {
                    return formatDoohVenues(entry.snapshot.target_dooh_venues_exclude);
                },
                getClipboardText(entry) {
                    return joinClipboardLines(
                        getDoohVenueItems(entry.snapshot.target_dooh_venues_exclude)
                    );
                },
            },
        ],
        isDOOHAd || !environmentSettings.canUseCarrierIsp()
            ? []
            : campaign.carriersISPsVersion === 1
            ? [
                  {
                      label: 'Target Carrier',
                      isCategory: false,
                      hasChange(entry) {
                          return hasTopLevelFieldsBeenModified(entry.patch, ['target_carriers']);
                      },
                      format(entry) {
                          return getTargetCarriers(entry.snapshot.target_carriers);
                      },
                      getClipboardText(entry) {
                          return this.format(entry);
                      },
                  },
              ]
            : [
                  {
                      label: 'Carrier / ISP',
                      isCategory: false,
                      useClipboardText: true,
                      hasChange(entry) {
                          return hasTopLevelFieldsBeenModified(entry.patch, ['target_carriers']);
                      },
                      format(entry) {
                          return getTargetCarriersInclude(
                              entry.snapshot.target_carriers,
                              entry.snapshot.platform
                          );
                      },
                      getClipboardText(entry) {
                          const text = this.format(entry);
                          // Only a real list is split; "All carriers / ISPs" and "None" stay one line.
                          return Array.isArray(entry.snapshot.target_carriers) &&
                              entry.snapshot.target_carriers.length > 0
                              ? joinClipboardLines(text.split(', '))
                              : text;
                      },
                  },
                  {
                      label: 'Carrier / ISP (Exclude)',
                      isCategory: false,
                      useClipboardText: true,
                      hasChange(entry) {
                          return hasTopLevelFieldsBeenModified(entry.patch, ['carriers_exclude']);
                      },
                      format(entry) {
                          return getTargetCarriersExclude(
                              entry.snapshot.carriers_exclude,
                              entry.snapshot.platform
                          );
                      },
                      getClipboardText(entry) {
                          const text = this.format(entry);
                          // Only a real list is split; "None" stays one line.
                          return Array.isArray(entry.snapshot.carriers_exclude) &&
                              entry.snapshot.carriers_exclude.length > 0
                              ? joinClipboardLines(text.split(', '))
                              : text;
                      },
                  },
              ],

        isTacticsEnabled(ad.tactics_enabled, campaign.id)
            ? []
            : campaign.isAppStoreCatEnabled
            ? [
                  {
                      label: 'App Categories (Include)',
                      isCategory: false,
                      useClipboardText: true,
                      hasChange(entry) {
                          return hasTopLevelFieldsBeenModified(entry.patch, [
                              'target_app_store_cat',
                          ]);
                      },
                      format(entry) {
                          if (!entry.snapshot.target_app_store_cat) {
                              return '';
                          }
                          return entry.snapshot.target_app_store_cat.join(', ');
                      },
                      getClipboardText(entry) {
                          return joinClipboardLines(entry.snapshot.target_app_store_cat);
                      },
                  },
                  {
                      label: 'App Categories (Exclude)',
                      isCategory: false,
                      useClipboardText: true,
                      hasChange(entry) {
                          return hasTopLevelFieldsBeenModified(entry.patch, [
                              'target_app_store_cat_exclude',
                          ]);
                      },
                      format(entry) {
                          if (!entry.snapshot.target_app_store_cat_exclude) {
                              return '';
                          }
                          return entry.snapshot.target_app_store_cat_exclude.join(', ');
                      },
                      getClipboardText(entry) {
                          return joinClipboardLines(entry.snapshot.target_app_store_cat_exclude);
                      },
                  },
              ]
            : [
                  {
                      label: 'IAB App Categories',
                      isCategory: false,
                      useClipboardText: true,
                      hasChange(entry) {
                          return hasTopLevelFieldsBeenModified(entry.patch, [
                              'target_iab_categories',
                          ]);
                      },
                      format(entry) {
                          return formatTargetIabCats(entry.snapshot.target_iab_categories);
                      },
                      getClipboardText(entry) {
                          return joinClipboardLines(
                              getTargetIabCatItems(entry.snapshot.target_iab_categories)
                          );
                      },
                  },
                  {
                      label: 'IAB App Categories (Exclude)',
                      isCategory: false,
                      useClipboardText: true,
                      hasChange(entry) {
                          return hasTopLevelFieldsBeenModified(entry.patch, [
                              'target_iab_categories_exclude',
                          ]);
                      },
                      format(entry) {
                          return formatTargetIabCats(entry.snapshot.target_iab_categories_exclude);
                      },
                      getClipboardText(entry) {
                          return joinClipboardLines(
                              getTargetIabCatItems(entry.snapshot.target_iab_categories_exclude)
                          );
                      },
                  },
                  {
                      label: 'Audience Segments Include',
                      isCategory: false,
                      useClipboardText: true,
                      hasChange(entry) {
                          return hasTopLevelFieldsBeenModified(entry.patch, ['audiences']);
                      },
                      format(entry) {
                          const audienceResources = _.get(storeState, 'resources.audiences', {});

                          return _.map(entry.snapshot.audiences, v => {
                              return _.get(audienceResources, `${v}.attr.name`);
                          }).join(', ');
                      },
                      getClipboardText(entry) {
                          const audienceResources = storeState?.resources?.audiences ?? {};

                          return joinClipboardLines(
                              (entry.snapshot.audiences ?? []).map(
                                  v => audienceResources[v]?.attr?.name
                              )
                          );
                      },
                  },
                  {
                      label: 'Audience Segments Exclude',
                      isCategory: false,
                      useClipboardText: true,
                      hasChange(entry) {
                          return hasTopLevelFieldsBeenModified(entry.patch, ['audience_exclude']);
                      },
                      format(entry) {
                          const audienceResources = _.get(storeState, 'resources.audiences', {});

                          return _.map(entry.snapshot.audience_exclude, v => {
                              return _.get(audienceResources, `${v}.attr.name`);
                          }).join(', ');
                      },
                      getClipboardText(entry) {
                          const audienceResources = storeState?.resources?.audiences ?? {};

                          return joinClipboardLines(
                              (entry.snapshot.audience_exclude ?? []).map(
                                  v => audienceResources[v]?.attr?.name
                              )
                          );
                      },
                  },
              ],
        [
            environmentSettings.canUseGenderTargeting() && {
                label: 'Gender',
                isCategory: false,
                useClipboardText: true,
                hasChange(entry) {
                    if (isDOOHAd || isCTVAd) {
                        return false;
                    }

                    return hasTopLevelFieldsBeenModified(entry.patch, ['target_genders']);
                },
                format(entry) {
                    return formatTargetGenders(entry.snapshot.target_genders);
                },
                getClipboardText(entry) {
                    return getTargetGenderClipboardText(entry.snapshot.target_genders);
                },
            },
            environmentSettings.canUseAgeTargeting() && {
                label: 'Age',
                isCategory: false,
                useClipboardText: true,
                hasChange(entry) {
                    if (isDOOHAd || isCTVAd) {
                        return false;
                    }

                    return hasTopLevelFieldsBeenModified(entry.patch, ['target_age_groups']);
                },
                format(entry) {
                    return formatAgeGroup(entry.snapshot.target_age_groups);
                },
                getClipboardText(entry) {
                    return getAgeGroupClipboardText(entry.snapshot.target_age_groups);
                },
            },
            environmentSettings.canUseGeoFencing() && {
                label: 'Geofencing',
                isCategory: false,
                useClipboardText: true,
                hasChange(entry) {
                    if (isCTVAd) {
                        return false;
                    }

                    return hasTopLevelFieldsBeenModified(entry.patch, ['geoboxes']);
                },
                format(entry) {
                    return (
                        <div className="ef3-historyTable_bodyCellGeoBox">
                            {_.map(entry.snapshot.geoboxes, (box, i) => {
                                const shorter = formatGeoboxCoordinates(box);
                                return (
                                    <div key={i}>
                                        E: {shorter.E}, W: {shorter.W}, S: {shorter.S}, N:{' '}
                                        {shorter.N}
                                    </div>
                                );
                            })}
                        </div>
                    );
                },
                getClipboardText(entry) {
                    // Each box lists E, W, S and N on their own lines.
                    return joinClipboardLines(
                        (entry.snapshot.geoboxes ?? []).flatMap(box => {
                            const shorter = formatGeoboxCoordinates(box);
                            return ['E', 'W', 'S', 'N'].map(key => `${key}: ${shorter[key]}`);
                        })
                    );
                },
            },
            isTacticsEnabled(ad.tactics_enabled, campaign.id) &&
                environmentSettings.canUseTactics() && {
                    label: 'Tactics',
                    isCategory: false,
                    useClipboardText: true,
                    hasChange(entry) {
                        return hasTopLevelFieldsBeenModified(entry.patch, ['tactics_generators']);
                    },
                    format(entry) {
                        return (
                            <React.Fragment>
                                {_.map(entry.snapshot.tactics_generators, tactic => {
                                    return (
                                        <React.Fragment key={tactic.id}>
                                            <Spacer type="small" />
                                            <Separator label={tactic.title} />
                                            <Spacer type="small" />

                                            {_.map(tactic.targeting, target => {
                                                switch (target.module) {
                                                    case 'categories': {
                                                        // Same Included/Excluded label the clipboard uses. A missing flag is not excluded.
                                                        const exclude = _.get(
                                                            target,
                                                            'draft.ias.contextualTargeting.exclude'
                                                        );
                                                        const includeExcludedLabel = contextualTargetingModeLabel(
                                                            exclude
                                                        );

                                                        const values = _.get(
                                                            target,
                                                            'draft.ias.contextualTargeting.value',
                                                            []
                                                        );
                                                        const labels = _.map(
                                                            values,
                                                            value =>
                                                                IAS_CONTEXTUAL_TARGETING_MAPPING[
                                                                    value
                                                                ]
                                                        );

                                                        return (
                                                            <div key={target.id}>
                                                                <div>App Categories (Include):</div>
                                                                <div>
                                                                    {_.get(
                                                                        target,
                                                                        'draft.target_app_store_cat',
                                                                        []
                                                                    ).join(', ')}
                                                                </div>
                                                                <div>App Categories (Exclude)</div>
                                                                <br />
                                                                <div>
                                                                    {_.get(
                                                                        target,
                                                                        'draft.target_app_store_cat_exclude',
                                                                        []
                                                                    ).join(', ')}
                                                                </div>
                                                                <div>Contextual Targeting:</div>
                                                                <div>{includeExcludedLabel}</div>
                                                                {labels.join(', ')}
                                                            </div>
                                                        );
                                                    }
                                                    case 'ias_contextual_targeting': {
                                                        // Same Included/Excluded label the clipboard uses. A missing flag is not excluded.
                                                        const exclude = _.get(
                                                            target,
                                                            'draft.ias.contextualTargeting.exclude'
                                                        );
                                                        const includeExcludedLabel = contextualTargetingModeLabel(
                                                            exclude
                                                        );

                                                        const values = _.get(
                                                            target,
                                                            'draft.ias.contextualTargeting.value',
                                                            []
                                                        );
                                                        const labels = _.map(
                                                            values,
                                                            value =>
                                                                IAS_CONTEXTUAL_TARGETING_MAPPING[
                                                                    value
                                                                ]
                                                        );

                                                        return (
                                                            <div key={target.id}>
                                                                <div>Contextual Targeting:</div>
                                                                <div>{includeExcludedLabel}</div>
                                                                {labels.join(', ')}
                                                            </div>
                                                        );
                                                    }
                                                    case 'app_categories': {
                                                        return (
                                                            <div key={target.id}>
                                                                <div>App Categories (Include):</div>
                                                                <div>
                                                                    {_.get(
                                                                        target,
                                                                        'draft.target_app_store_cat',
                                                                        []
                                                                    ).join(', ')}
                                                                </div>
                                                                <div>App Categories (Exclude)</div>
                                                                <div>
                                                                    {_.get(
                                                                        target,
                                                                        'draft.target_app_store_cat_exclude',
                                                                        []
                                                                    ).join(', ')}
                                                                </div>
                                                            </div>
                                                        );
                                                    }

                                                    case 'audience_segments': {
                                                        const audienceResources = _.get(
                                                            storeState,
                                                            'audienceSegmentPicker.audienceSegments',
                                                            {}
                                                        );
                                                        const audiencesInclude = _.map(
                                                            _.get(target, 'draft.audiences', []),
                                                            v => {
                                                                return _.result(
                                                                    _.find(audienceResources, {
                                                                        id: v,
                                                                    }),
                                                                    'name'
                                                                );
                                                            }
                                                        ).join(', ');

                                                        const audiencesExclude = _.map(
                                                            _.get(
                                                                target,
                                                                'draft.audience_exclude',
                                                                []
                                                            ),
                                                            v => {
                                                                return _.result(
                                                                    _.find(audienceResources, {
                                                                        id: v,
                                                                    }),
                                                                    'name'
                                                                );
                                                            }
                                                        ).join(', ');

                                                        let geoFencingResult = null;
                                                        if (
                                                            target.draft &&
                                                            target.draft.geo_targeting_settings
                                                        ) {
                                                            const geoFencingOutput = getGeofencing(
                                                                target.draft.geo_targeting_settings
                                                            );

                                                            geoFencingResult = (
                                                                <div
                                                                    className="ef3-historyTable_bodyCell_geoTargeting"
                                                                    key={target.id}
                                                                >
                                                                    <div>POI Segments:</div>
                                                                    {_.map(
                                                                        geoFencingOutput,
                                                                        (v, i) => (
                                                                            <p key={i}>{v}</p>
                                                                        )
                                                                    )}
                                                                </div>
                                                            );
                                                        }

                                                        return (
                                                            <React.Fragment>
                                                                <div key={target.id}>
                                                                    <div>
                                                                        Audience Segments Include:
                                                                    </div>
                                                                    <div>{audiencesInclude}</div>
                                                                    <div>
                                                                        Audience Segments Exclude:
                                                                    </div>
                                                                    <div>{audiencesExclude}</div>
                                                                </div>
                                                                <Spacer />
                                                                {geoFencingResult}
                                                            </React.Fragment>
                                                        );
                                                    }

                                                    case 'pois': {
                                                        const geoFencingOutput = getGeofencing(
                                                            target.draft.geo_targeting_settings
                                                        );

                                                        return (
                                                            <div
                                                                className="ef3-historyTable_bodyCell_geoTargeting"
                                                                key={target.id}
                                                            >
                                                                <div>POI Segments:</div>
                                                                {_.map(geoFencingOutput, (v, i) => (
                                                                    <p key={i}>{v}</p>
                                                                ))}
                                                            </div>
                                                        );
                                                    }

                                                    default:
                                                        return null;
                                                }
                                            })}

                                            <Spacer />
                                        </React.Fragment>
                                    );
                                })}
                            </React.Fragment>
                        );
                    },
                    getClipboardText(entry) {
                        return joinClipboardLines(
                            (entry.snapshot.tactics_generators ?? []).flatMap(tactic => [
                                tactic.title,
                                ...(tactic.targeting ?? []).flatMap(target =>
                                    getTacticTargetClipboardLines(target, storeState)
                                ),
                            ])
                        );
                    },
                },
            environmentSettings.canUseWeatherConditions() && {
                label: 'Weather Conditions',
                isCategory: false,
                useClipboardText: true,
                hasChange(entry) {
                    return hasTopLevelFieldsBeenModified(entry.patch, ['weatherConditions']);
                },
                format(entry) {
                    if (campaign.isWeatherConditionEnabled) {
                        // Create weather conditions data object for WeatherConditionSummary
                        const weatherConditionsData = {
                            getConditionLabel: getConditionLabel(storeState),
                            getQualifierLabel: getQualifierLabel(storeState),
                            getValueLabel: getValueLabel(storeState),
                            getTimeframeLabel: getTimeframeLabel(storeState),
                            hasWeatherConditions: getHasWeatherConditions(storeState),
                            inputAdornmentMap: getInputAdornmentMap(storeState),
                            isWeatherTypeTemperature: getIsWeatherTypeTemperature(storeState),
                        };

                        return getHasWeatherConditions(storeState)(
                            entry.snapshot.weatherConditions
                        ) ? (
                            <React.Fragment>
                                {_.map(
                                    entry.snapshot.weatherConditions.conditions,
                                    (condition, index) => (
                                        <WeatherConditionHistorySummary
                                            key={condition.id || index}
                                            condition={condition}
                                            title={`Condition ${index + 1}`}
                                            showDivider={
                                                index !==
                                                entry.snapshot.weatherConditions.conditions.length -
                                                    1
                                            }
                                            logicRelationship={
                                                entry.snapshot.weatherConditions.logicRelationship
                                            }
                                            weatherConditionsData={weatherConditionsData}
                                        />
                                    )
                                )}
                            </React.Fragment>
                        ) : (
                            <React.Fragment>None</React.Fragment>
                        );
                    }
                },
                /**
                 * Copies the new Weather Conditions only, one label per line.
                 * Matches the Rotation Method clipboard: a heading, then Condition,
                 * Qualifier, Value, and Time Frame. OR / AND stays between conditions.
                 *
                 * @param {Object} entry - History entry for the current snapshot.
                 * @returns {string}
                 */
                getClipboardText(entry) {
                    if (!campaign?.isWeatherConditionEnabled) {
                        return '';
                    }
                    const weatherConditionsData = {
                        getConditionLabel: getConditionLabel(storeState),
                        getQualifierLabel: getQualifierLabel(storeState),
                        getValueLabel: getValueLabel(storeState),
                        getTimeframeLabel: getTimeframeLabel(storeState),
                        inputAdornmentMap: getInputAdornmentMap(storeState),
                        isWeatherTypeTemperature: getIsWeatherTypeTemperature(storeState),
                    };
                    return formatWeatherConditionsClipboardText(
                        entry.snapshot.weatherConditions,
                        weatherConditionsData
                    );
                },
            },
        ]
    );
    const doohPublishers = storeState?.resources?.doohPublishers || [];
    fields.push(
        {
            label: 'Inventory',
            isCategory: true,
            hasChange(entry) {
                const fieldsToCheck = [
                    'dealApplicationType',
                    'applistSettings',
                    'exchanges_blocked_ui',
                ];

                if (isDOOHAd) {
                    fieldsToCheck.push('target_dooh_publishers', 'target_dooh_publishers_exclude');
                }

                return hasTopLevelFieldsBeenModified(entry.patch, fieldsToCheck);
            },
            format() {
                return '';
            },
        },
        {
            label: 'Public Inventory',
            isCategory: false,
            hasChange(entry) {
                return hasTopLevelFieldsBeenModified(entry.patch, ['dealApplicationType']);
            },
            format(entry) {
                if (entry.snapshot.dealApplicationType === 'deal_only') {
                    return 'Do not bid on open auctions';
                }
                return 'Bid on open auctions';
            },
            getClipboardText(entry) {
                return this.format(entry);
            },
        },
        {
            label: flags.isEnabled('efb_35_curated_deals') ? 'Deals' : 'Private Deals',
            isCategory: false,
            useClipboardText: true,
            hasChange(entry) {
                return hasTopLevelFieldsBeenModified(entry.patch, ['referenced_deals']);
            },
            prepData(entry) {
                const { referenced_deals } = entry.snapshot;
                return referenced_deals?.map(deal => deal?.app_name);
            },
            format(entry) {
                const appNames = this.prepData(entry);
                return (
                    <div>
                        {appNames?.map((app_name, index) => <div key={index}>{app_name}, </div>)}
                    </div>
                );
            },
            getClipboardText(entry) {
                // One app name per line, without the trailing comma shown on screen.
                return joinClipboardLines(this.prepData(entry));
            },
        },
        {
            label: 'DOOH Publishers Include',
            isCategory: false,
            useClipboardText: true,
            hasChange(entry) {
                if (!isDOOHAd) {
                    return false;
                }

                return hasTopLevelFieldsBeenModified(entry.patch, ['target_dooh_publishers']);
            },
            format(entry) {
                return getDoohPublisherNamesFromIds(
                    entry.snapshot.target_dooh_publishers,
                    doohPublishers
                ).join(', ');
            },
            getClipboardText(entry) {
                return joinClipboardLines(
                    getDoohPublisherNamesFromIds(
                        entry.snapshot.target_dooh_publishers,
                        doohPublishers
                    )
                );
            },
        },
        {
            label: 'DOOH Publishers Exclude',
            isCategory: false,
            useClipboardText: true,
            hasChange(entry) {
                if (!isDOOHAd) {
                    return false;
                }

                return hasTopLevelFieldsBeenModified(entry.patch, [
                    'target_dooh_publishers_exclude',
                ]);
            },
            format(entry) {
                return getDoohPublisherNamesFromIds(
                    entry.snapshot.target_dooh_publishers_exclude,
                    doohPublishers
                ).join(', ');
            },
            getClipboardText(entry) {
                return joinClipboardLines(
                    getDoohPublisherNamesFromIds(
                        entry.snapshot.target_dooh_publishers_exclude,
                        doohPublishers
                    )
                );
            },
        }
    );
    if (campaign.isExchangeTargetingEnabled && environmentSettings.canUseExchanges()) {
        fields.push({
            label: 'Exchanges(Exclude)',
            isCategory: false,
            useClipboardText: true,
            hasChange(entry) {
                return hasTopLevelFieldsBeenModified(entry.patch, ['exchanges_blocked_ui']);
            },
            format(entry) {
                const values = entry.snapshot.exchanges_blocked_ui;

                if (!values || values.length === 0) {
                    return 'None';
                }

                return getExchangeLabels(values).join(', ');
            },
            getClipboardText(entry) {
                const values = entry.snapshot.exchanges_blocked_ui;

                // "None" stays one line.
                if (!values || values.length === 0) {
                    return this.format(entry);
                }

                return joinClipboardLines(getExchangeLabels(values));
            },
        });
    }
    fields.push({
        label: environmentSettings.getAppsAndSitesLabel(),
        isCategory: false,
        useClipboardText: true,
        hasChange(entry) {
            return hasTopLevelFieldsBeenModified(entry.patch, [
                'applistSettings',
                'whitelistedAppsAndSites',
                'blacklistedAppsAndSites',
            ]);
        },
        format(entry) {
            const lists = getAppsAndSitesLists(entry, storeState);
            if (!lists) {
                return '';
            }
            const { appLists, whiteList, blackList } = lists;

            return (
                <React.Fragment>
                    <IncludeExcludeLayout type="Include" label="Allowed" marginBottom compact>
                        {formatApplistEntries({
                            listIds: whiteList,
                            appLists,
                            emptyMessage: 'All Apps & Sites',
                        })}
                    </IncludeExcludeLayout>
                    <IncludeExcludeLayout type="Exclude" label="Blocked" compact>
                        {formatApplistEntries({
                            listIds: blackList,
                            appLists,
                            emptyMessage: 'None',
                        })}
                    </IncludeExcludeLayout>
                </React.Fragment>
            );
        },
        getClipboardText(entry) {
            const lists = getAppsAndSitesLists(entry, storeState);
            if (!lists) {
                return '';
            }
            const { appLists, whiteList, blackList } = lists;

            // Headings stay; each app list name is its own line.
            return [
                'Allowed:',
                getApplistClipboardText({
                    listIds: whiteList,
                    appLists,
                    emptyMessage: 'All Apps & Sites',
                }),
                'Blocked:',
                getApplistClipboardText({
                    listIds: blackList,
                    appLists,
                    emptyMessage: 'None',
                }),
            ].join('\n');
        },
    });
    fields.push(
        {
            label: 'Bid Optimization',
            isCategory: true,
            hasChange(entry) {
                return (
                    hasTopLevelFieldsBeenModified(entry.patch, [
                        'max_cpm_rate_local',
                        'use_deal_price_to_bid',
                        'bid_strategy_mode',
                        'automatic_bid_price.max_ecpm_local',
                    ]) || hasFrequencyCapVisibleChange(entry)
                );
            },
            format() {
                return '';
            },
        },
        {
            label: 'Bid Strategy',
            isCategory: false,
            hasChange(entry) {
                // Only care about changes to user-visible field max_ecpm_local, not calculated fields like max_ecpm
                return hasTopLevelFieldsBeenModified(entry.patch, [
                    'bid_strategy_mode',
                    'automatic_bid_price.max_ecpm_local',
                ]);
            },
            format(entry) {
                if (entry.snapshot.bid_strategy_mode === 'fixed_bid_price') {
                    return 'Fixed bid price';
                } else {
                    return `Automated bid price. Do not exceed ${formatNumber_currency(
                        entry.snapshot.automatic_bid_price.max_ecpm_local
                    )}  eCPM`;
                }
            },
            getClipboardText(entry) {
                return this.format(entry);
            },
        },
        {
            label: 'Total Bid Price',
            isCategory: false,
            hasChange(entry) {
                return hasTopLevelFieldsBeenModified(entry.patch, ['max_cpm_rate_local']);
            },
            format(entry) {
                const { max_cpm_rate_local, automatic_bid_price } = entry.snapshot;

                const isAutoBidMode = entry.snapshot.bid_strategy_mode === 'automatic_bid_price';

                if (max_cpm_rate_local > 0) {
                    return formatNumber_currency(max_cpm_rate_local);
                }

                return isAutoBidMode
                    ? formatNumber_currency(automatic_bid_price.max_ecpm_local)
                    : 'Unlimited';
            },
            getClipboardText(entry) {
                return this.format(entry);
            },
        },
        {
            label: 'Use Deal Price To Bid',
            isCategory: false,
            hasChange(entry) {
                return hasTopLevelFieldsBeenModified(entry.patch, ['use_deal_price_to_bid']);
            },
            format(entry) {
                const { use_deal_price_to_bid } = entry.snapshot;

                if (_.isNil(use_deal_price_to_bid)) {
                    return 'N/A';
                }

                return use_deal_price_to_bid
                    ? 'Use Total Bid Price for targeted deals'
                    : "Use Private Deal's specified price for targeted deals";
            },
            getClipboardText(entry) {
                return this.format(entry);
            },
        },
        {
            label: 'Frequency Cap',
            isCategory: false,
            hasChange(entry) {
                return hasFrequencyCapVisibleChange(entry);
            },
            formatPrevious(entry) {
                return formatFrequencyCapFromSnapshot(entry._previousSnapshot);
            },
            format(entry) {
                return formatFrequencyCapFromSnapshot(entry.snapshot);
            },
            getClipboardText(entry) {
                return this.format(entry).toString();
            },
        }
    );
    return _(fields)
        .filter(field => field)
        .value();
}

function getGeofencing(settings) {
    if (!settings) return [];

    const { categories, custom_layers, category_layers } = settings;

    // Explicitly handle the case where targeting is effectively disabled:
    if (categories.length === 0 && custom_layers.length === 0 && category_layers.length === 0) {
        return [];
    }

    const adGeoCategoryLayers = createSummaryString(
        [].concat(categories),
        'POI Category',
        'POI Categories'
    );
    const adGeoLayers = createSummaryString(
        [].concat(category_layers),
        'Standard POI Segment',
        'Standard POI Segments'
    );
    const adCustomLayers = createSummaryString(
        custom_layers,
        'Custom POI Segment',
        'Custom POI Segments'
    );

    return [].concat(
        adGeoCategoryLayers,
        adGeoLayers,
        adCustomLayers,
        `Retarget - ${settings.retarget}`,
        `Target - ${settings.target}`
    );

    function createSummaryString(list, name, pluralName) {
        if (!list.length) return [];
        return `${list.length} ${list.length > 1 ? pluralName : name}`;
    }
}

/**
 * True when the ad targets every age group, including an empty selection.
 *
 * @param {Array<number>} val - Stored age group codes.
 * @returns {boolean}
 */
const isEveryoneAgeGroup = val => val.length === 0 || val.length === 7;

/**
 * Display labels for the selected age groups. Unknown codes are skipped.
 *
 * @param {Array<number>} val - Stored age group codes.
 * @returns {string[]}
 */
function getAgeGroupItems(val) {
    var displayTextArray = [];
    for (const item of val) {
        switch (item) {
            case 2:
                displayTextArray.push('19-24');
                break;
            case 3:
                displayTextArray.push('25-34');
                break;
            case 4:
                displayTextArray.push('35+');
                break;
            case 5:
                displayTextArray.push('35-44');
                break;
            case 6:
                displayTextArray.push('45-54');
                break;
            case 7:
                displayTextArray.push('55-64');
                break;
            case 8:
                displayTextArray.push('65+');
                break;
            case -1:
                displayTextArray.push('Unknown');
                break;
        }
    }
    return displayTextArray;
}

/**
 * On-screen age text. `Everyone` stays one line; otherwise the groups are comma-separated.
 *
 * @param {Array<number>} val - Stored age group codes.
 * @returns {string}
 */
function formatAgeGroup(val) {
    return isEveryoneAgeGroup(val) ? 'Everyone' : getAgeGroupItems(val).join(', ');
}

/**
 * Clipboard age text. `Everyone` stays one line; a list is one group per line.
 *
 * @param {Array<number>} val - Stored age group codes.
 * @returns {string}
 */
function getAgeGroupClipboardText(val) {
    return isEveryoneAgeGroup(val) ? 'Everyone' : joinClipboardLines(getAgeGroupItems(val));
}

/**
 * Display names for the selected device language codes.
 *
 * @param {Array<string>|*} obj - Language codes, or a non-array when none are set.
 * @returns {string[]}
 */
function getDeviceLanguageItems(obj) {
    return Array.isArray(obj) ? obj.map(item => LANGUAGE_CODES[item]) : [];
}

/**
 * On-screen device language text, comma-separated.
 *
 * @param {Array<string>|*} obj - Language codes.
 * @returns {string}
 */
function formatDeviceLanguage(obj) {
    return getDeviceLanguageItems(obj).join(', ');
}

/**
 * Display names for gender codes M, F, and D.
 *
 * @param {Array<string>} val - Gender codes.
 * @returns {string[]}
 */
function getTargetGenderItems(val) {
    return val.map(function(item) {
        if (item === 'M') {
            return 'Male';
        } else if (item === 'F') {
            return 'Female';
        } else if (item === 'D') {
            return 'Unknown';
        }
        throw new Error('Gender array is not empthy and contains unregonize value');
    });
}

/**
 * On-screen gender text. An empty list is `Everyone`.
 *
 * @param {Array<string>|*} val - Gender codes.
 * @returns {string|undefined}
 */
function formatTargetGenders(val) {
    if (Array.isArray(val)) {
        if (val.length === 0) {
            return 'Everyone';
        }
        return getTargetGenderItems(val).join(', ');
    }
}

/**
 * Clipboard gender text. `Everyone` stays one line; a list is one gender per line.
 *
 * @param {Array<string>|*} val - Gender codes.
 * @returns {string|undefined}
 */
function getTargetGenderClipboardText(val) {
    if (Array.isArray(val)) {
        if (val.length === 0) {
            return 'Everyone';
        }
        return joinClipboardLines(getTargetGenderItems(val));
    }
}

/**
 * Display names for the selected IAB category ids.
 *
 * @param {Array<string>|*} obj - IAB category ids.
 * @returns {string[]}
 */
function getTargetIabCatItems(obj) {
    return Array.isArray(obj) ? obj.map(i => IABCATEGORY_TARGETING[i]) : [];
}

/**
 * On-screen IAB category text, comma-separated.
 *
 * @param {Array<string>|*} obj - IAB category ids.
 * @returns {string}
 */
function formatTargetIabCats(obj) {
    return getTargetIabCatItems(obj).join(', ');
}

/**
 * Merges dayparts into display ranges.
 *
 * @param {Object} snapshot - Ad snapshot with `dayparts` or `dayparts_local`.
 * @param {string} timezone - Timezone used to convert the ranges.
 * @param {string} startDate - Ad start date, used for the timezone abbreviation.
 * @returns {{ ranges: Array<{start: string, end: string}>, formattedTimezone: string }|null}
 *   Null when the ad runs the full day.
 */
function getDaypartRanges(snapshot, timezone, startDate) {
    const dayparts = snapshot.dayparts ? snapshot.dayparts : snapshot.dayparts_local;
    if (
        dayparts === undefined ||
        dayparts === null ||
        (Array.isArray(dayparts) ? dayparts.length === 0 : Object.keys(dayparts).length === 0)
    ) {
        return null;
    }
    const formattedTimezone = snapshot.dayparts
        ? moment(startDate)
              .tz(timezone)
              .format('z')
        : 'Local Time';
    // since database converts automatically, there is no need for conversion
    const daypartsByTimezone = convertDaypartsByTimezone(dayparts, 'UTC', timezone, startDate);
    return { ranges: mergeTimeranges(daypartsByTimezone), formattedTimezone };
}

/**
 * On-screen dayparts. Full day is one span; otherwise one list item per range.
 *
 * @param {Object} snapshot - Ad snapshot with `dayparts` or `dayparts_local`.
 * @param {string} timezone - Timezone used to convert the ranges.
 * @param {string} startDate - Ad start date, used for the timezone abbreviation.
 * @returns {JSX.Element}
 */
function formatDayparts(snapshot, timezone, startDate) {
    const daypartRanges = getDaypartRanges(snapshot, timezone, startDate);
    if (!daypartRanges) {
        return <span>Full day</span>;
    }
    const { ranges, formattedTimezone } = daypartRanges;
    const out = (ranges ?? []).map((part, index) => {
        return (
            <li key={index}>
                {part.start}
                <span> {formattedTimezone}</span>
                &nbsp;~ {part.end}
                <span> {formattedTimezone}</span>
            </li>
        );
    });
    return <ul className="list-unstyled">{out}</ul>;
}

/**
 * Clipboard dayparts. `Full day` stays one line. Each range is its own line.
 *
 * @param {Object} snapshot - Ad snapshot with `dayparts` or `dayparts_local`.
 * @param {string} timezone - Timezone used to convert the ranges.
 * @param {string} startDate - Ad start date, used for the timezone abbreviation.
 * @returns {string}
 */
function formatDaypartsClipboardText(snapshot, timezone, startDate) {
    const daypartRanges = getDaypartRanges(snapshot, timezone, startDate);
    if (!daypartRanges) {
        return 'Full day';
    }
    const { ranges, formattedTimezone } = daypartRanges;
    return joinClipboardLines(
        (ranges ?? []).map(
            part => `${part.start} ${formattedTimezone} ~ ${part.end} ${formattedTimezone}`
        )
    );
}

/**
 * Day names that are turned on, Monday through Sunday.
 *
 * @param {Array<boolean>} weekparts - Seven flags, Monday first.
 * @returns {string[]}
 */
function getWeekpartItems(weekparts) {
    var names = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    return (weekparts ?? [])
        .map(function(bool, idx) {
            return bool ? names[idx] : '';
        })
        .filter(Boolean);
}

/**
 * On-screen weekparts. The timezone suffix stays in front of a comma-separated day list.
 *
 * @param {Array<boolean>} weekparts - Seven flags, Monday first.
 * @param {string} suffix - Timezone label, including its trailing space.
 * @returns {string}
 */
function formatWeekparts(weekparts, suffix) {
    return suffix + getWeekpartItems(weekparts).join(', ');
}

/**
 * Clipboard weekparts. The timezone label is a heading line, then one day per line.
 *
 * @param {Array<boolean>} weekparts - Seven flags, Monday first.
 * @param {string} suffix - Timezone label, including its trailing space.
 * @returns {string}
 */
function getWeekpartsClipboardText(weekparts, suffix) {
    return joinClipboardLines([suffix.trim(), ...getWeekpartItems(weekparts)]);
}

/**
 * Display names for the selected DOOH venue type ids.
 *
 * @param {Array<string>|*} obj - Venue type ids.
 * @returns {string[]}
 */
function getDoohVenueItems(obj) {
    return Array.isArray(obj) ? obj.map(i => DOOH_VENUE_TYPES_NO_PARENT[i]) : [];
}

/**
 * On-screen DOOH venue text, comma-separated.
 *
 * @param {Array<string>|*} obj - Venue type ids.
 * @returns {string}
 */
function formatDoohVenues(obj) {
    return getDoohVenueItems(obj).join(', ');
}

/**
 * Formats each geobox coordinate to two decimals.
 *
 * @param {Object} box - Geobox with E, W, S and N coordinates.
 * @returns {Object} The same keys as fixed-point strings.
 */
function formatGeoboxCoordinates(box) {
    const shorter = {};
    Object.keys(box).forEach(function(key) {
        shorter[key] = box[key].toFixed(2);
    });
    return shorter;
}

/**
 * Sorted display names for excluded exchange ids.
 *
 * @param {Array<string>} values - Exchange ids.
 * @returns {string[]}
 */
function getExchangeLabels(values) {
    return values.map(value => labelExchange(value)).sort();
}

/**
 * Resolves the allowed and blocked app list ids for the Apps and Sites row.
 *
 * @param {Object} entry - History entry.
 * @param {Object} storeState - Redux store state holding the app lists.
 * @returns {{ appLists: Array, whiteList: Array, blackList: Array }|null} Null when no app lists are loaded.
 */
function getAppsAndSitesLists(entry, storeState) {
    const appLists = Object.values(storeState?.resources?.appLists ?? {}).map(list => list.attr);
    if (appLists.length === 0) {
        return null;
    }
    let whiteList = [];
    let blackList = [];

    const isUsingSeparateAppsAndSitesField = entry.snapshot.usingSeparateApplistFields;

    if (isUsingSeparateAppsAndSitesField) {
        whiteList = entry.snapshot.whitelistedAppsAndSites;
        blackList = entry.snapshot.blacklistedAppsAndSites;
    } else {
        if (entry.snapshot.applistSettings.mode === 'whitelist') {
            whiteList = entry.snapshot.applistSettings.listIds;
        } else {
            blackList = entry.snapshot.applistSettings.listIds;
        }
    }

    return { appLists, whiteList, blackList };
}

/**
 * A non-empty list or any other truthy flag is excluded. Missing, false, and [] are not.
 *
 * @param {*} exclude - draft.ias.contextualTargeting.exclude
 * @returns {boolean}
 */
function isContextualTargetingExcluded(exclude) {
    if (Array.isArray(exclude)) {
        return exclude.length > 0;
    }
    return Boolean(exclude);
}

/**
 * Label shown on the history row and copied for Contextual Targeting.
 * Matches the ad summary: a real exclude flag is Excluded, otherwise Included.
 *
 * @param {*} exclude - draft.ias.contextualTargeting.exclude
 * @returns {string} Excluded or Included
 */
function contextualTargetingModeLabel(exclude) {
    return isContextualTargetingExcluded(exclude) ? 'Excluded' : 'Included';
}

/**
 * Plain-text lines for one tactic target. Headings stay; each list item is its own line.
 *
 * @param {Object} target - Tactic targeting module.
 * @param {Object} storeState - Redux store state holding the audience segments.
 * @returns {Array<string>} Lines in display order.
 */
function getTacticTargetClipboardLines(target, storeState) {
    const readPath = (source, path) =>
        path.split('.').reduce((current, key) => current?.[key], source);
    const getList = path => {
        const value = readPath(target, path);
        return Array.isArray(value) ? value : [];
    };
    const contextualTargetingLines = () => {
        // Heading, then the same Included/Excluded word the row shows, then one value per line.
        const labels = getList('draft.ias.contextualTargeting.value').map(
            value => IAS_CONTEXTUAL_TARGETING_MAPPING[value]
        );
        return [
            'Contextual Targeting:',
            contextualTargetingModeLabel(readPath(target, 'draft.ias.contextualTargeting.exclude')),
            ...labels,
        ];
    };
    const appCategoryLines = () => [
        'App Categories (Include):',
        ...getList('draft.target_app_store_cat'),
        'App Categories (Exclude):',
        ...getList('draft.target_app_store_cat_exclude'),
    ];
    const poiLines = settings => ['POI Segments:', ...getGeofencing(settings)];

    switch (target.module) {
        case 'categories':
            return [...appCategoryLines(), ...contextualTargetingLines()];
        case 'ias_contextual_targeting':
            return contextualTargetingLines();
        case 'app_categories':
            return appCategoryLines();
        case 'audience_segments': {
            const audienceResources = storeState?.audienceSegmentPicker?.audienceSegments ?? {};
            const audienceNames = path =>
                getList(path).map(id => {
                    const segments = Array.isArray(audienceResources)
                        ? audienceResources
                        : Object.values(audienceResources);
                    const match = segments.find(segment => segment?.id === id);
                    const name = match?.name;
                    return typeof name === 'function' ? name.call(match) : name;
                });

            return [
                'Audience Segments Include:',
                ...audienceNames('draft.audiences'),
                'Audience Segments Exclude:',
                ...audienceNames('draft.audience_exclude'),
                ...(readPath(target, 'draft.geo_targeting_settings')
                    ? poiLines(target.draft.geo_targeting_settings)
                    : []),
            ];
        }
        case 'pois':
            return poiLines(target.draft.geo_targeting_settings);
        default:
            return [];
    }
}
